/**
 * Buy Used Cars in India — Guide Page
 * Route: /buy-used-cars
 * Purpose: High-value static content page for AdSense & SEO
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Car,
  CheckCircle2,
  AlertTriangle,
  IndianRupee,
  FileText,
  Shield,
  Search,
  ChevronRight,
} from 'lucide-react';

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'How to Buy Used Cars in India — Complete Buyer\'s Guide',
  description: 'A comprehensive guide to buying second-hand cars in India — what to check, how to negotiate, RC transfer, insurance, and how to avoid scams.',
  author: { '@type': 'Organization', name: 'MemAds' },
  publisher: {
    '@type': 'Organization',
    name: 'MemAds',
    logo: { '@type': 'ImageObject', url: 'https://memads.in/android-chrome-192x192.png' },
  },
  datePublished: '2026-01-15',
  dateModified: '2026-08-01',
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://memads.in/buy-used-cars' },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What documents should I check when buying a used car in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Always verify: RC (Registration Certificate), insurance policy, PUC certificate, Form 35 (if on loan), NOC from bank if the loan is cleared, and road tax receipts. Ensure the chassis and engine numbers on the RC match the physical vehicle.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does RC transfer cost in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'RC transfer fees vary by state but typically range from ₹300 to ₹600 for the RTO fee. The process requires Form 29 and Form 30 signed by both buyer and seller. The transfer should be completed within 30 days of purchase.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is it safe to buy used cars on MemAds?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. MemAds has verified seller profiles, transparent listing history, and a secure chat system so you can ask questions before meeting. Always inspect the car in person in a safe, public location and complete payment only after completing all paperwork.',
      },
    },
  ],
};

const checklistItems = [
  { label: 'Check RC (Registration Certificate) — verify name, engine & chassis number match', critical: true },
  { label: 'Verify insurance validity and type (third-party or comprehensive)', critical: true },
  { label: 'Check PUC (Pollution Under Control) certificate is current', critical: false },
  { label: 'Confirm no outstanding loan — ask for bank NOC or Form 35', critical: true },
  { label: 'Run a full road test: engine start, acceleration, brakes, steering', critical: true },
  { label: 'Check for hidden body dents, repaint patches, or rust on chassis', critical: false },
  { label: 'Inspect tyre condition (tread depth) and spare tyre', critical: false },
  { label: 'Test all electronics: AC, windows, music system, sensors', critical: false },
  { label: 'Check engine bay for oil leaks, coolant levels, and belt condition', critical: true },
  { label: 'Get an independent inspection from a certified mechanic', critical: true },
];

const BuyUsedCarsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background" id="buy-used-cars-page">
      <SEO
        title="Buy Used Cars in India — Complete Buyer's Guide | MemAds"
        description="Learn how to safely buy a used car in India. Check RC documents, negotiate price, avoid scams, and complete RC transfer. Browse verified used car listings on MemAds."
        canonical="/buy-used-cars"
        jsonLd={[guideJsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-rose-900 py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(225,29,72,0.15),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center">
              <Car className="h-6 w-6 text-rose-400" />
            </div>
            <span className="text-xs font-semibold text-rose-400 uppercase tracking-widest">Buyer's Guide</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
            How to Buy Used Cars in India — Complete Guide
          </h1>
          <p className="text-base text-white/70 max-w-2xl leading-relaxed">
            Buying a second-hand car in India can save you 30–50% compared to a new vehicle — but only if you know what to look for. This guide covers everything from document verification to price negotiation and RC transfer.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">

        {/* Why Buy Used */}
        <section id="why-buy-used-cars">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Why Buy a Used Car in India?
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              India's used car market is one of the fastest growing in Asia, with over 4 million pre-owned vehicles sold every year. The primary appeal is value — a well-maintained 3-year-old car can be purchased at 50–60% of its original showroom price, with the steepest depreciation already absorbed by the first owner.
            </p>
            <p>
              Beyond savings, the used car segment in India has matured significantly. Organized players, multi-brand showrooms, and platforms like MemAds have increased transparency, giving buyers access to vehicle history, inspection reports, and verified seller credentials before making a decision.
            </p>
            <p>
              For first-time car buyers, a used vehicle is often a smarter entry point — you can learn your preferences in a real city driving environment without the financial pressure of a new car loan.
            </p>
          </div>
        </section>

        {/* Checklist */}
        <section id="used-car-checklist" className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Complete Used Car Inspection Checklist
          </h2>
          <p className="text-xs text-muted-foreground mb-5">Use this checklist before finalizing any used car purchase in India:</p>
          <div className="space-y-2.5">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-background border border-border">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${item.critical ? 'text-rose-500' : 'text-green-500'}`} />
                <span className="text-sm text-foreground">
                  {item.label}
                  {item.critical && <span className="ml-2 text-xs font-semibold text-rose-500 bg-rose-50 dark:bg-rose-950/30 px-1.5 py-0.5 rounded">Critical</span>}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Documents Section */}
        <section id="used-car-documents">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Essential Documents to Verify
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                title: 'RC (Registration Certificate)',
                desc: 'The most important document. Match the chassis number, engine number, owner name, and address against the physical vehicle. Also check the fuel type (petrol/diesel/CNG) and vehicle class.',
              },
              {
                title: 'Insurance Certificate',
                desc: 'Verify the insurance is still valid and covers third-party at minimum. A lapsed insurance means the car cannot be legally driven. Check if it is comprehensive or third-party only.',
              },
              {
                title: 'PUC Certificate',
                desc: 'Pollution Under Control certificate is mandatory. Check the expiry date. Most petrol stations and RTOs issue them for ₹50–₹100. An expired PUC will get the car challan-ed.',
              },
              {
                title: 'Form 29 & Form 30',
                desc: 'These are the NOC and transfer of ownership forms. Both must be signed by the seller. Without these, you cannot transfer the RC to your name at the RTO.',
              },
              {
                title: 'Bank NOC / Form 35',
                desc: 'If the car was purchased on a loan, the bank\'s name appears on the RC under "Hypothecation." Get a bank NOC and Form 35 confirming the loan is fully repaid before buying.',
              },
              {
                title: 'Road Tax Receipts',
                desc: 'Road tax is a one-time or periodic payment depending on the state. Verify the receipts are present to avoid future disputes with the RTO or at inter-state check posts.',
              },
            ].map((doc) => (
              <div key={doc.title} className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-semibold text-sm text-foreground mb-1.5">{doc.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{doc.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Price Negotiation */}
        <section id="used-car-price-negotiation">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            How to Negotiate the Price
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              Price negotiation is expected in India's used car market. Sellers typically list 10–20% above their actual minimum price, leaving room for discussion. Here's how to negotiate effectively:
            </p>
            <ul className="space-y-2 not-prose">
              {[
                'Research the market value: Check similar cars (same make, model, year, variant, fuel type, colour) on MemAds and other platforms. This gives you a data-backed negotiation anchor.',
                'Use inspection findings as leverage: If the car needs new tyres, brake pads, or has minor dents, calculate the repair cost and ask the seller to reduce the price accordingly.',
                'Point out the depreciation curve: Cars depreciate 15–25% in Year 1 and 10–15% per year after. Use depreciation calculators to show the logical fair value.',
                'Make a firm but polite offer: Start 12–15% below the asking price. Give the seller a reason to accept — "I can pay today, with full documentation."',
                'Be ready to walk away: The strongest negotiation position is genuine willingness to leave. It signals you are serious and have alternatives.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Scam Warning */}
        <section id="used-car-scam-warning" className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Common Scams to Avoid
          </h2>
          <div className="space-y-3">
            {[
              { title: 'Tampered odometer', desc: 'Demand the service history to cross-check mileage at each service. A 5-year-old car with 20,000 km is a red flag.' },
              { title: 'Duplicate RC / stolen vehicle', desc: 'Run the vehicle number on the Vahan portal (vahan.parivahan.gov.in) to verify ownership, registration details, and blacklist status.' },
              { title: 'Advance payment scams', desc: 'Never pay a booking amount or "token money" to hold a car without physically seeing it and verifying all documents first.' },
              { title: 'Flood-damaged cars', desc: 'Check for water marks inside the dashboard, rust under the carpet, or musty smell. Flood-damaged cars have serious electrical issues over time.' },
            ].map((scam) => (
              <div key={scam.title} className="flex gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{scam.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{scam.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* RC Transfer */}
        <section id="rc-transfer-process">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            RC Transfer Process in India
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              Transferring the RC (Registration Certificate) to your name is a legal requirement under the Motor Vehicles Act. The process must be completed within 30 days of purchase to avoid penalties.
            </p>
            <p>
              <strong className="text-foreground">Documents needed for RC transfer:</strong> Signed Form 29 (Notice of transfer) and Form 30 (Report of transfer) from the seller, original RC, valid insurance certificate, PUC certificate, your address proof (Aadhaar/passport/utility bill), your ID proof, passport-size photos, and the applicable RTO fee (₹300–₹600 depending on the state).
            </p>
            <p>
              <strong className="text-foreground">Online RC transfer:</strong> Many states now allow RC transfer applications through the Parivahan Sewa portal. You may still need to visit the RTO for physical verification of the vehicle in some states.
            </p>
            <p>
              Until the RC is in your name, you are not the legal owner. If the car is involved in any accident or violation, the liability can still trace back to the previous owner — a source of legal complications for both parties.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="used-car-faq">
          <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqJsonLd.mainEntity.map((faq, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold text-sm text-foreground mb-2">{faq.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-primary/10 to-rose-500/10 border border-primary/20 rounded-2xl p-8 text-center">
          <Search className="h-8 w-8 text-primary mx-auto mb-3" />
          <h2 className="text-xl font-bold text-foreground mb-2">Ready to Find Your Next Car?</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Browse thousands of verified used car listings across India. Filter by city, price, and make — and connect directly with sellers.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/listings?category=vehicles"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-md hover:brightness-105 transition"
              id="browse-used-cars-cta"
            >
              <Car className="h-4 w-4" /> Browse Used Cars
            </Link>
            <Link
              to="/post"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:bg-accent transition"
              id="sell-car-cta"
            >
              Sell Your Car
            </Link>
          </div>
        </section>

        {/* Related Guides */}
        <section id="related-guides">
          <h3 className="text-base font-semibold text-foreground mb-3">Related Guides</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/buy-used-mobiles" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Mobiles Guide
            </Link>
            <Link to="/buy-used-furniture" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Furniture Guide
            </Link>
            <Link to="/blog/how-to-write-perfect-classified-ad" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> How to Write a Great Ad
            </Link>
            <Link to="/safety" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Safety Tips
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BuyUsedCarsPage;
